import { Dependent } from "elephant-web/gql";
import { useIntl } from "react-intl";
import { type Asserts, array, number, object, string } from "yup";
import {
  BALANCE,
  DEPENDENT,
  INSURER_DETAILS,
  INSURER_DETAILS_NAME,
  INSURER_NAME,
  INSURER_TYPE,
  INSURERS,
  MEMBERSHIP_NUMBER,
  NATIONAL_ID_NUMBER,
  NHIF,
  PROPERTIES,
  TOTAL,
} from "./formConstants";

export const useValidationSchema = (totalAmount: number) => {
  const { formatMessage } = useIntl();
  return object({
    [INSURERS]: array()
      .of(
        object({
          [INSURER_TYPE]: string().required(),
          [INSURER_NAME]: string().required(),
          [PROPERTIES]: array().of(object({})),
          [BALANCE]: object({
            [TOTAL]: number()
              .required(
                formatMessage({
                  id: "invoice.insurer.balance.required",
                  defaultMessage: "Please enter an amount",
                }),
              )
              .integer()
              .positive(
                formatMessage({
                  id: "invoicing.insurers.balance.error",
                  defaultMessage: "Must be greater than 0",
                }),
              )
              .test({
                name: "maxTotal",
                exclusive: false,
                params: { totalAmount },
                message: formatMessage({
                  id: "invoice.insurer.balance.max",
                  defaultMessage: "Insurance cannot exceed total",
                }),
                test: (_, context) => {
                  const currentInsurers =
                    // @ts-expect-error Legacy reason unknown
                    context?.options?.from[2]?.value[INSURERS];
                  const currentPaidTotal = currentInsurers?.reduce(
                    (a, b) => a + b.balance.total,
                    0,
                  );
                  return currentPaidTotal <= totalAmount;
                },
              }),
          }),
          [INSURER_DETAILS]: object({
            [INSURER_DETAILS_NAME]: string(),
            [MEMBERSHIP_NUMBER]: string().required(),
            [DEPENDENT]: string().when(INSURER_NAME, {
              is: (name) => name === NHIF,
              then: string()
                .oneOf(
                  [
                    Dependent.Member,
                    Dependent.Dependentchild,
                    Dependent.Dependentspouse,
                    Dependent.Spouse,
                    Dependent.Child,
                  ],
                  formatMessage({
                    id: "invoicing.insurers.dependent.error",
                    defaultMessage:
                      "You must select one of the values from the Dependents dropdown menu",
                  }),
                )
                .required(
                  formatMessage({
                    id: "invoicing.insurers.dependent.required.error",
                    defaultMessage:
                      "You must select one of the values from the Dependents dropdown menu",
                  }),
                ),
              otherwise: string().nullable(),
            }),
            [NATIONAL_ID_NUMBER]: string().nullable(),
          }),
        }),
      )
      .nullable(),
  });
};

export type FormValues = Asserts<ReturnType<typeof useValidationSchema>>;
